/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { setDocumentsCount } from 'redux/actions/resultActions';

import { setCulture, L10n, createElement } from '@syncfusion/ej2-base';
import { TextBox } from '@syncfusion/ej2-react-inputs';
import { TextField } from 'shared/components/form/Inputs';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { colors } from 'shared/defaultTheme';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Inject,
  Filter,
  Resize,
} from '@syncfusion/ej2-react-grids';
import useApi from 'hooks/useApi';
import {
  getIconAccessDocument,
  getIconCode,
  getIconDoc,
  getIconExcel,
  getIconImage,
  getIconPdf,
  getIconPpt,
  getIconSound,
  getIconVideo,
  getIconZip,
  getBestFontContrast,
  capitalizeFirstLetter,
} from 'shared/utils';
import { ReactComponent as DocumentIcon } from 'assets/icon-document.svg';
import { ReactComponent as PdfIcon } from 'assets/icon-pdf.svg';
import { ReactComponent as WordIcon } from 'assets/icon-word.svg';
import { ReactComponent as ImgIcon } from 'assets/icon-img.svg';
import { ReactComponent as ExcelIcon } from 'assets/icon-excel.svg';
import { ReactComponent as HtmlIcon } from 'assets/icon-html.svg';
import { ReactComponent as SoundIcon } from 'assets/icon-sound.svg';
import { ReactComponent as VideoIcon } from 'assets/icon-video.svg';
import { ReactComponent as AccessDocumentIcon } from 'assets/icon-access-document.svg';
import { ReactComponent as PowerPointIcon } from 'assets/icon-ppt.svg';
import { ReactComponent as ZipIcon } from 'assets/icon-zip.svg';
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons';
import { removeBreadcrumb } from 'redux/actions/layoutActions';
import { getDocumentDetails, getDocumentPath, getPathName } from 'redux/actions/documentActions';
import { MultiSelect, CheckBoxSelection } from '@syncfusion/ej2-react-dropdowns';
import { DataUtil } from '@syncfusion/ej2-data';
import PropTypes from 'prop-types';
import { selectCurrentClient } from 'redux/actions/clientActions';
import { DocumentationResultsWrapper } from '../styles';
import { L10nObj } from '../contants';

DataUtil.serverTimezoneOffset = 2 * (new Date().getTimezoneOffset() / 60);
MultiSelect.Inject(CheckBoxSelection);

const DocumentResults = forwardRef(({ documentTypes, getCounters }, ref) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState();
  const { isCustomer, cusNavId, mail, clientId } = useSelector((state) => state.auth);
  const { generalSearchText } = useSelector((state) => state.results);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const { breadcrumbs } = useSelector((state) => state.layout);
  const { customAreaApi } = useApi();
  const [searchValue, setSearchValue] = useState('');
  const [language, setLanguage] = useState('es');
  const [pageSettings, setPageSettings] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const [tagsList, setTagsList] = useState(null);
  const [statesList, setStatesList] = useState(null);
  const [isPublicOptions, setIsPublicOptions] = useState(null);
  const grid = useRef(null);

  let data;
  let dropInstance;

  const [, getDocuments] = customAreaApi(
    {
      url: `${window.API_V1}/cli/items/${cusNavId}/${encodeURIComponent(mail)}/search/all`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getDocumentsCount] = customAreaApi(
    {
      url: `${window.API_V1}/cli/items/${cusNavId}/${encodeURIComponent(mail)}/search/all/count`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getOnePathItem] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const [, getTags] = customAreaApi(
    {
      url: `${window.API_V1}/cus/tags/${cusNavId}/search`,
      method: 'POST',
      data: {
        filters: [
          {
            model: 'Tag',
            field: 'Name',
            asc: false,
          },
        ],
        reduce: {
          limit: 0,
          skip: 0,
        },
      },
    },
    { manual: true }
  );

  const [, getStates] = customAreaApi(
    {
      url: `${window.API_V1}/cus/states/${cusNavId}/search`,
      method: 'POST',
      data: {
        filters: [
          {
            model: 'State',
            field: 'Name',
            asc: false,
          },
        ],
        reduce: {
          limit: 0,
          skip: 0,
        },
      },
    },
    { manual: true }
  );

  const [, getClient] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const getClientById = async (id) => {
    try {
      const { data: clientData } = await getClient({
        url: `${window.API_V1}/cli/clients/${cusNavId}/${id}`,
      });
      return clientData?.data;
    } catch (error) {
      console.error(`Error getting client: ${error}`);
      return error;
    }
  };

  const getStatesData = async () => {
    const {
      data: { data: states },
    } = await getStates();

    const stateNames =
      states.map((x) => {
        if (!x.i18List) return null;
        const state = x.i18List.find((y) => y.i18Code === selectedDefaultLanguage);
        return {
          text: state?.i18Name || x.i18List[0]?.i18Name || '',
          id: x.id,
        };
      }) || [];
    stateNames.sort((a, b) => a.text.localeCompare(b.text));
    setStatesList(stateNames);
  };

  const getTagsData = async () => {
    const {
      data: { data: tags },
    } = await getTags();
    const tagNames = tags
      .map((x) => {
        if (!x.i18List) {
          return null;
        }
        const tag = x.i18List.find((y) => y.i18Code === selectedDefaultLanguage);
        return {
          text: tag?.i18Name || x.i18List[0]?.i18Name || '',
          id: x.id,
        };
      })
      .filter((tag) => tag !== null);

    tagNames.sort((a, b) => a.text.localeCompare(b.text));
    setTagsList(tagNames);
  };

  useEffect(() => {
    L10n.load(L10nObj);
    const lang = selectedDefaultLanguage.slice(0, 2);
    setLanguage(lang);
    setCulture(lang);
    getTagsData();
    getStatesData();
    setIsPublicOptions([
      { id: true, text: t('general_button_yes') },
      { id: false, text: t('general_button_no') },
    ]);
    return () => {
      setTagsList(null);
      setStatesList(null);
    };
  }, [selectedDefaultLanguage]);

  const gridIsPublicTemplate = (args) => {
    const { isPublic } = args;
    return (
      <div
        className={classNames('public-wrapper', {
          'public-yes': isPublic,
          'public-no': !isPublic,
        })}
      >
        {isPublic ? t('general_button_yes') : t('general_button_no')}
      </div>
    );
  };

  const gridFileTypeTemplate = (args) => {
    const { type } = args;
    if (getIconDoc(type)) {
      return <WordIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconPdf(type)) {
      return <PdfIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconImage(type)) {
      return <ImgIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconCode(type)) {
      return <HtmlIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconSound(type)) {
      return <SoundIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconVideo(type)) {
      return <VideoIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconAccessDocument(type)) {
      return (
        <AccessDocumentIcon
          fill={colors.suvaGrey}
          className="control-panel__icon-type"
          width="20"
        />
      );
    }
    if (getIconPpt(type)) {
      return (
        <PowerPointIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />
      );
    }
    if (getIconZip(type)) {
      return <ZipIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconExcel(type)) {
      return <ExcelIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    return <DocumentIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
  };

  const gridClassificationTemplate = (args) => {
    let count;
    const { tagsData } = args;
    if (!tagsData) count = 0;
    const tagNames =
      tagsData.map((x) => {
        const tag = x.i18List.find((y) => y.i18Code === selectedDefaultLanguage);
        return tag?.i18Name || x.i18List[0]?.i18Name || '';
      }) || [];
    count = tagNames.length;
    const items = tagNames.map((x) => {
      return {
        text: x,
      };
    });

    return (
      <div className={classNames('classificationWrapper')}>
        <DropDownButtonComponent cssClass="e-caret-hide" id="classification-button" items={items}>
          {count}
        </DropDownButtonComponent>
      </div>
    );
  };

  const gridStatusTemplate = (args) => {
    const { stateData } = args;
    if (stateData) {
      const { i18List } = stateData;
      const state = i18List.find((x) => x.i18Code === selectedDefaultLanguage) || i18List[0];
      return (
        <div
          className="status-wrapper"
          style={{
            backgroundColor: stateData.color,
            color: getBestFontContrast(stateData.color),
          }}
        >
          {state.i18Name}
        </div>
      );
    }
    return null;
  };

  const setLoader = (value) => {
    if (!document.querySelector('.e-spinner-pane')) return;
    if (value) {
      document.querySelector('.e-spinner-pane').style.display = 'block';
    } else {
      document.querySelector('.e-spinner-pane').style.display = 'none';
    }
  };

  const getFilters = (state) => {
    const filtering = state?.where?.[0].predicates || [];

    const finalFilters = [];
    filtering.forEach((x) => {
      if (x.field === 'tags') {
        const tagNames = x.value.split(',');
        const tagIds = tagsList.filter((tag) => tagNames.includes(tag.text)).map((tag) => tag.id);
        x.value = tagIds;
        finalFilters.push(x);
      }
      if (x.field === 'state') {
        const stateNames = x.value.split(',');
        const stateIds = statesList.filter((s) => stateNames.includes(s.text)).map((s) => s.id);
        x.value = stateIds;
        finalFilters.push(x);
      }
      if (x.field === 'isPublic') {
        const isPublicNames = x.value.split(',');
        const isPublicValues = isPublicOptions
          .filter((s) => isPublicNames.includes(s.text))
          .map((s) => s.id);

        if (isPublicValues.length === 2) return;

        isPublicValues.forEach((value) => {
          const filter = { ...x, value };
          finalFilters.push(filter);
        });
      }
      if (x.field === 'type') {
        const types = x.value.split(',');
        x.value = types;
        finalFilters.push(x);
      }
      if (!['tags', 'state', 'isPublic', 'type'].includes(x.field)) {
        finalFilters.push(x);
      }
    });
    return finalFilters;
  };

  const getData = async (state) => {
    setLoader(true);
    let filtering;
    let sortingFilter = sorting;

    if (state?.action?.action === 'filter' || state?.action?.action === 'clearFilter') {
      filtering = getFilters(state);
      setFilters(filtering);
    }

    const currentFilters = filtering || filters;

    const dataFilters = currentFilters.map((x) => {
      if (
        x.predicates &&
        x.predicates.length > 1 &&
        (x.predicates[0].field === 'created' || x.predicates[0].field === 'modified')
      ) {
        return {
          model: 'PathItem',
          field: x.predicates[0].field.charAt(0).toUpperCase() + x.predicates[0].field.slice(1),
          filter: [x.predicates[0].value, x.predicates[1].value],
          isGreaterThan: true,
        };
      }

      if (x.field === 'type' && Array.isArray(x.value) && x.value.length > 0 && x.value[0] !== '') {
        return {
          model: 'PathItem',
          field: 'Type',
          filter: x.value,
        };
      }

      if (x.field === 'tags' && Array.isArray(x.value) && x.value.length > 0 && x.value[0] !== '') {
        return {
          model: 'PathItem',
          field: 'Tags',
          filter: x.value,
        };
      }

      if (
        x.field === 'state' &&
        Array.isArray(x.value) &&
        x.value.length > 0 &&
        x.value[0] !== ''
      ) {
        return {
          model: 'PathItem',
          field: 'State',
          filter: x.value,
        };
      }

      if (x.field === 'isPublic') {
        return {
          model: 'PathItem',
          field: 'IsPublic',
          filter: [!!x.value],
        };
      }

      let filter = {
        model: 'PathItem',
        field: x.field.charAt(0).toUpperCase() + x.field.slice(1),
        filter: [x.value],
        IsRegexDiacritics: true,
      };
      if (x?.operator === 'greaterthan') {
        filter = { ...filter, isGreaterThan: true };
      }
      if (x?.operator === 'lessthan') {
        filter = { ...filter, isLessThan: true };
      }
      if (x?.operator === 'contains') {
        filter = { ...filter, IsRegexDiacritics: true };
      }
      return filter;
    });

    if (state?.action?.requestType === 'sorting' && state?.action?.columnName) {
      sortingFilter = {
        model: 'PathItem',
        field: capitalizeFirstLetter(state?.action?.columnName),
        asc: state?.action?.direction === 'Ascending',
      };
    }

    if (state?.action?.requestType === 'sorting' && !state?.action?.columnName) {
      sortingFilter = null;
    }

    setSorting(sortingFilter);

    dataFilters.push(
      sortingFilter || {
        model: 'PathItem',
        field: 'Created',
        asc: false,
      }
    );

    dataFilters.push({
      model: 'PathItem',
      field: 'IsFile',
      filter: [true],
    });

    if (!isCustomer) {
      dataFilters.push({
        model: 'PathItem',
        field: 'ClientId',
        filter: [clientId],
      });
    }

    const body = {
      filters: dataFilters,
      reduce: {
        limit: state.take,
        skip: state.skip / pageSize + 1,
      },
      ...((searchValue || generalSearchText) && {
        filterText: {
          regexFilter: searchValue || generalSearchText,
        },
      }),
    };

    try {
      const {
        data: { data: count },
      } = await getDocumentsCount({
        data: body,
      });

      const {
        data: { data: documents },
      } = await getDocuments({
        data: body,
      });

      dispatch(setDocumentsCount(count));
      setLoader(false);

      const documentsData = documents?.map((e) => ({
        ...e,
        tags: e.tags.toString(),
        state: e.state || '',
      }));

      return {
        count: count || 0,
        result: documentsData || [],
      };
    } catch (error) {
      console.error(`Error Contacts: ${error}`);
      return {
        count: 0,
        result: [],
      };
    }
  };

  const dataStateChange = async (state) => {
    let dataSource;
    setPageSize(state.take);
    const requestType = state?.action?.requestType;
    if (requestType === 'stringfilterrequest') return;
    if (requestType === 'filterchoicerequest') {
      dataSource = await getData({ skip: 0, take: pageSize });
    } else {
      dataSource = await getData(state);
    }

    if (grid.current) {
      grid.current.dataSource = dataSource;
    }
  };

  useEffect(() => {
    L10n.load(L10nObj);
    const fetchData = async () => {
      const state = { skip: 0, take: pageSize };
      await dataStateChange(state);
    };
    setPageSettings({ pageCount: 3, pageSize, pageSizes: [5, 10, 20, 50, 75, 100] });
    fetchData();
  }, []);

  const getFilter = (field) => {
    const filter = filters.find((x) => x.field === field);
    return filter?.value || '';
  };

  const customFilter = {
    ui: {
      create: (args) => {
        document.getElementsByClassName('e-flm_optrdiv')[0].style.display = 'none';
        document.getElementsByClassName('e-flmenu-valuediv')[0].style.padding = '0';
        const flValInput = createElement('input', { className: 'flm-input' });
        args.target.appendChild(flValInput);
        dropInstance = new TextBox({
          type: 'text',
          placeholder: t('results_filter_search_value'),
          value: getFilter(args.column.field),
        });
        dropInstance.appendTo(flValInput);
      },
      read: (args) => {
        grid.current.removeFilteredColsByField(args.column.field);
        args.fltrObj.filterByColumn(args.column.field, 'contains', dropInstance.value);
      },
      write: (args) => {
        if (args.filteredValue) {
          dropInstance.value = args.filteredValue;
        }
      },
    },
  };

  const classificationFilter = {
    ui: {
      create: (args) => {
        document.getElementsByClassName('e-flm_optrdiv')[0].style.display = 'none';
        document.getElementsByClassName('e-flmenu-valuediv')[0].style.padding = '0';
        const flValInput = createElement('input', { className: 'flm-input' });
        args.target.appendChild(flValInput);
        dropInstance = new MultiSelect({
          dataSource: tagsList,
          fields: { text: 'text', value: 'id' },
          placeholder: t('results_filter_search_value'),
          mode: 'CheckBox',
          filtering: (e) => {
            const query = new RegExp(e.text, 'i');
            e.updateData(tagsList.filter((x) => query.test(x.text)));
          },
        });
        dropInstance.appendTo(flValInput);
      },
      read: (args) => {
        args.fltrObj.filterByColumn(args.column.field, 'contains', dropInstance.text);
      },
      write: (args) => {
        dropInstance.text = args.filteredValue || '';
      },
    },
  };

  const documentTypesFilter = {
    ui: {
      create: (args) => {
        document.getElementsByClassName('e-flm_optrdiv')[0].style.display = 'none';
        document.getElementsByClassName('e-flmenu-valuediv')[0].style.padding = '0';
        const flValInput = createElement('input', { className: 'flm-input' });
        args.target.appendChild(flValInput);
        dropInstance = new MultiSelect({
          dataSource: documentTypes,
          fields: { text: 'text', value: 'id' },
          placeholder: t('results_filter_search_value'),
          mode: 'CheckBox',
          filtering: (e) => {
            const query = new RegExp(e.text, 'i');
            e.updateData(documentTypes.filter((x) => query.test(x.text)));
          },
        });
        dropInstance.appendTo(flValInput);
      },
      read: (args) => {
        args.fltrObj.filterByColumn(args.column.field, 'contains', dropInstance.text);
      },
      write: (args) => {
        dropInstance.text = args.filteredValue || '';
      },
    },
  };

  const stateFilter = {
    ui: {
      create: (args) => {
        document.getElementsByClassName('e-flm_optrdiv')[0].style.display = 'none';
        document.getElementsByClassName('e-flmenu-valuediv')[0].style.padding = '0';
        const flValInput = createElement('input', { className: 'flm-input' });
        args.target.appendChild(flValInput);
        dropInstance = new MultiSelect({
          dataSource: statesList,
          fields: { text: 'text', value: 'id' },
          placeholder: t('results_filter_search_value'),
          mode: 'CheckBox',
          filtering: (e) => {
            const query = new RegExp(e.text, 'i');
            e.updateData(statesList.filter((x) => query.test(x.text)));
          },
        });
        dropInstance.appendTo(flValInput);
      },
      read: (args) => {
        args.fltrObj.filterByColumn(args.column.field, 'contains', dropInstance.text);
      },
      write: (args) => {
        dropInstance.text = args.filteredValue || '';
      },
    },
  };

  const isPublicFilter = {
    ui: {
      create: (args) => {
        document.getElementsByClassName('e-flm_optrdiv')[0].style.display = 'none';
        document.getElementsByClassName('e-flmenu-valuediv')[0].style.padding = '0';
        const flValInput = createElement('input', { className: 'flm-input' });
        args.target.appendChild(flValInput);
        dropInstance = new MultiSelect({
          dataSource: isPublicOptions,
          fields: { text: 'text', value: 'id' },
          placeholder: t('results_filter_search_value'),
          mode: 'CheckBox',
          filtering: (e) => {
            const query = new RegExp(e.text, 'i');
            e.updateData(isPublicOptions.filter((x) => query.test(x.text)));
          },
        });
        dropInstance.appendTo(flValInput);
      },
      read: (args) => {
        args.fltrObj.filterByColumn(args.column.field, 'contains', dropInstance.text);
      },
      write: (args) => {
        dropInstance.text = args.filteredValue || '';
      },
    },
  };

  useImperativeHandle(ref, () => ({
    async doSearch() {
      const state = { skip: 0, take: pageSize };
      await getCounters();
      await dataStateChange(state);
    },
  }));

  const handleSearch = (e) => {
    if (e) {
      const { value } = e.target;
      setSearchValue(value);
    } else {
      setSearchValue('');
    }
  };

  const handleRowSelected = async (row) => {
    const { data: currentDocument, target } = row;
    if (target.id === 'classification-button') return;
    if (breadcrumbs.length > 1) {
      breadcrumbs.forEach(() => {
        dispatch(removeBreadcrumb());
      });
    }
    localStorage.removeItem('documentFormState');
    localStorage.removeItem('localizacionPath');
    localStorage.removeItem('setPath');
    const { clientNavisionId, clientId: selectedClientId, id } = currentDocument;
    try {
      const {
        data: { data: documentData },
      } = await getOnePathItem({
        url: `${
          window.API_V1
        }/cli/items/${clientNavisionId}/${selectedClientId}/${encodeURIComponent(mail)}/${id}`,
      });
      const pathIds = documentData.pathId.split('/');
      const clearedPathIds = pathIds.filter((x) => x.length > 0);
      clearedPathIds.splice(-1);
      let currentPath = '';
      clearedPathIds.forEach((pathName) => {
        currentPath += `${pathName}/`;
      });
      const findClient = await getClientById(currentDocument.clientId);
      dispatch(selectCurrentClient(findClient));
      currentDocument.tags = currentDocument?.tags?.split(',') || [];
      localStorage.setItem('documentFormState', JSON.stringify(currentDocument));
      localStorage.setItem('localizacionPath', documentData.pathName);
      localStorage.setItem('setPath', currentPath);
      dispatch(getDocumentPath(currentPath));
      dispatch(getPathName(documentData.pathName));
      dispatch(getDocumentDetails(documentData));
      history.push('/documentation', {
        params: { documentType: 'new_word', documentData },
      });
    } catch (error) {
      console.error('getting get one path item error: ', error);
    }
  };

  const onSearch = async () => {
    const state = { skip: 0, take: pageSize };
    await dataStateChange(state);
  };

  const onInputChange = async (e) => {
    if (e.key === 'Enter') {
      await onSearch();
    }
  };

  useEffect(() => {
    (async () => {
      if (searchValue === '') {
        const state = { skip: 0, take: pageSize };
        await dataStateChange(state);
      }
    })();
  }, [searchValue]);

  return (
    <DocumentationResultsWrapper>
      <div className="filter-container">
        <div className="form flex-column" noValidate>
          <div className="d-flex flex-row align-items-center h-50 ml-filter-section selector-filter-by-wrapper" />
          <TextField
            borderRadius={2}
            className="mb-4 m-0 h-50 filter-input"
            inputClassName="w-100"
            withButtonClear
            rightIcon={
              <SearchIcon fill={colors.black} onClick={onSearch} style={{ cursor: 'pointer' }} />
            }
            placeholder={t('results_search_placeholder_documentation')}
            value={searchValue ?? ''}
            onChange={handleSearch}
            onKeyDown={onInputChange}
          />
        </div>
      </div>

      <div className="control-pane">
        <div className="control-section">
          <GridComponent
            id="Grid"
            dataSource={data}
            ref={(g) => {
              grid.current = g;
            }}
            allowPaging
            pageSettings={pageSettings}
            dataStateChange={dataStateChange}
            allowSorting
            allowFiltering
            filterSettings={{
              type: 'Menu',
              ignoreAccent: true,
              operators: {
                dateOperator: [
                  { value: 'equal', text: t('filters.equal') },
                  { value: 'greaterthan', text: t('filters.greaterthan') },
                  { value: 'lessthan', text: t('filters.lessthan') },
                ],
              },
            }}
            locale={language}
            rowSelected={(args) => {
              handleRowSelected(args);
            }}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="type"
                headerTemplate={t('panel_table_type')}
                width="90"
                textAlign="Left"
                template={gridFileTypeTemplate}
                filter={documentTypesFilter}
              />
              <ColumnDirective
                field="name"
                headerText={t('panel_table_name')}
                minWidth="200"
                width="auto"
                textAlign="Left"
                filter={customFilter}
              />
              <ColumnDirective
                field="created"
                headerText={t('panel_table_height')}
                width="160"
                textAlign="Left"
                type="date"
                format={{ type: 'date', format: 'dd/MM/yyyy HH:mm' }}
              />
              <ColumnDirective
                field="modified"
                headerText={t('panel_table_date')}
                width="180"
                textAlign="Left"
                type="date"
                format={{ type: 'date', format: 'dd/MM/yyyy HH:mm' }}
              />
              <ColumnDirective
                field="tags"
                headerText={t('documents_search_classification')}
                width="170"
                textAlign="Left"
                template={gridClassificationTemplate}
                filter={classificationFilter}
              />
              <ColumnDirective
                field="state"
                headerText={t('documents_search_status')}
                width="120"
                textAlign="Left"
                template={gridStatusTemplate}
                filter={stateFilter}
              />
              <ColumnDirective
                field="isPublic"
                headerText={t('panel_table_public')}
                width="120"
                textAlign="Left"
                template={gridIsPublicTemplate}
                filter={isPublicFilter}
              />
            </ColumnsDirective>
            <Inject services={[Page, Filter, Resize]} />
          </GridComponent>
        </div>
        <div id="waitingpopup" className="waitingpopup">
          <span id="gif" className="image" />
        </div>
      </div>
    </DocumentationResultsWrapper>
  );
});

DocumentResults.propTypes = {
  getCounters: PropTypes.func.isRequired,
};

export default DocumentResults;
